import React, { useEffect, useState } from 'react';
import {
  Typography,
  Input,
  Pagination,
  Button,
  Row,
  Col
} from 'antd';
import { SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchProducts } from '../store/slices/productsSlice';
import { fetchCategories } from '../store/slices/categoriesSlice';
import ProductList from '../components/products/ProductList';

const { Title, Text } = Typography;

const ProductsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, loading: productsLoading, pagination } = useSelector((state) => state.products);
  const { categories } = useSelector((state) => state.categories);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const categoryId = searchParams.get('category') === 'all' ? null : searchParams.get('category');
  const isOnSale = searchParams.get('sale') === 'true';

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    const params = {
      page,
      limit: 20,
      ...(searchQuery && { search: searchQuery }),
      ...(categoryId && { category: categoryId }),
      ...(isOnSale && { isOnSale: true }),
    };

    dispatch(fetchProducts(params));
  }, [dispatch, page, searchQuery, categoryId, isOnSale]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setPage(1);

    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleCategoryClick = (id) => {
    const params = new URLSearchParams(searchParams);
    params.set('category', id.toString());
    params.set('page', '1');
    setSearchParams(params);
    setPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handlePageChange = (page) => {
    setPage(page);
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const findCategory = (cats, id) => {
    for (const cat of cats) {
      if (cat.id.toString() === id.toString()) return cat;
      if (cat.children) {
        const found = findCategory(cat.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const activeCategory = categoryId ? findCategory(categories, categoryId) : null;

  const isAncestor = (cat, targetId) => {
    if (!targetId) return false;
    if (cat.id.toString() === targetId.toString()) return true;
    if (!cat.children) return false;
    return cat.children.some(child => isAncestor(child, targetId));
  };

  const renderCategoryTree = (cats, level = 0) => {
    return cats.map(cat => {
      const isExpanded = isAncestor(cat, categoryId);
      const isActive = categoryId && cat.id.toString() === categoryId.toString();

      return (
        <div key={cat.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          <Button
            type="text"
            onClick={() => handleCategoryClick(cat.id)}
            style={{ 
              textAlign: 'left', 
              height: 'auto', 
              padding: '8px 12px', 
              borderRadius: 10, 
              justifyContent: 'flex-start', 
              fontWeight: isActive ? 700 : 500, 
              color: isActive ? '#0ea5e9' : '#334155',
              background: isActive ? '#e0f2fe' : (level === 0 ? '#f8fafc' : 'transparent'),
              border: isActive ? '1px solid #bae6fd' : '1px solid transparent',
              width: '100%',
              display: 'block',
              whiteSpace: 'normal',
              lineHeight: 1.4
            }}
          >
            {cat.name}
          </Button>
          {cat.children && cat.children.length > 0 && isExpanded && (
            <div style={{ borderLeft: '2px solid #e2e8f0', marginLeft: 16, paddingLeft: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {renderCategoryTree(cat.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{ width: '100%', padding: '24px 20px', maxWidth: 1920, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {activeCategory && activeCategory.parent_id && (
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => handleCategoryClick(activeCategory.parent_id)}
              title="Orqaga"
              shape="round"
            >
              Orqaga
            </Button>
          )}
          <Title level={2} style={{ margin: 0, fontFamily: 'Outfit', fontWeight: 800, color: '#f8fafc' }}>
            {isOnSale ? "Chegirmadagi Mahsulotlar" : (activeCategory ? activeCategory.name : 'Katalog')}
          </Title>
        </div>
      </div>

      <Row gutter={[32, 24]}>
        {/* Left Sidebar for Categories */}
        <Col xs={24} lg={6} xl={5} xxl={4}>
          <div style={{ position: 'sticky', top: 24, paddingBottom: 65 }}>
            <div className="glass" style={{ padding: '20px', borderRadius: '24px' }}>
              <Title level={4} style={{ fontFamily: 'Outfit', fontWeight: 800, marginBottom: 16, color: '#1e293b' }}>
                Filtirlash
              </Title>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button 
                  type={!activeCategory && !isOnSale ? "primary" : "default"} 
                  style={{ textAlign: 'left', height: 'auto', padding: '10px 16px', borderRadius: 12, justifyContent: 'flex-start', fontWeight: 600, border: 'none', background: !activeCategory && !isOnSale ? 'linear-gradient(135deg, #0ea5e9, #6366f1)' : '#f1f5f9', color: !activeCategory && !isOnSale ? 'white' : '#475569' }}
                  onClick={() => navigate('/products')}
                  block
                >
                  Barcha Mahsulotlar
                </Button>

                <Button 
                  type={isOnSale ? "primary" : "default"} 
                  style={{ 
                    textAlign: 'left', 
                    height: 'auto', 
                    padding: '10px 16px', 
                    borderRadius: 12, 
                    justifyContent: 'flex-start', 
                    fontWeight: 600, 
                    border: 'none', 
                    background: isOnSale ? 'linear-gradient(135deg, #ef4444, #f43f5e)' : '#fff1f2', 
                    color: isOnSale ? 'white' : '#e11d48' 
                  }}
                  onClick={() => navigate('/products?sale=true')}
                  block
                >
                  🔥 Chegirmadagilar
                </Button>

                <div style={{ borderTop: '1px solid rgba(0,0,0,0.05)', margin: '12px 0' }} />
                
                <Text style={{ fontSize: '0.85rem', marginBottom: 4, paddingLeft: 8, fontWeight: 600, color: '#64748b' }}>
                  Turlar ro&apos;yxati
                </Text>
                
                {renderCategoryTree(categories.filter(c => !c.parent_id))}
              </div>
            </div>
          </div>
        </Col>

        {/* Right Main Content for Products */}
        <Col xs={24} lg={18} xl={19} xxl={20}>
          {/* Search */}
          <div style={{ marginBottom: 24 }}>
            <Input
              size="large"
              placeholder="Mahsulot qidirish..."
              value={searchQuery}
              onChange={handleSearchChange}
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              style={{ borderRadius: 20, padding: '12px 24px', fontSize: '1.05rem', boxShadow: '0 8px 16px rgba(0,0,0,0.04)', border: '1px solid rgba(255,255,255,0.8)' }}
              allowClear
            />
          </div>

          {/* Products Grid */}
          <ProductList products={products} loading={productsLoading} />

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40, marginBottom: 40 }}>
              <Pagination
                current={page}
                total={pagination.total || 0}
                pageSize={20}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ProductsPage;
